import * as React from "react"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"

interface StepperProps {
  steps: Record<string, { title: string; description: string }>
  activeStep: string
  className?: string
}

interface StepProps {
  isActive: boolean
  isCompleted: boolean
  isLast: boolean
  stepNumber: number
  stepTitle?: string
  totalSteps?: number
  className?: string
}

const Step = React.forwardRef<HTMLDivElement, StepProps>(
  ({ isActive, isCompleted, isLast, stepNumber, stepTitle, totalSteps, className, ...props }, ref) => {
    const statusText = isCompleted
      ? 'completed'
      : isActive
        ? 'current'
        : 'upcoming'

    return (
      <div
        ref={ref}
        className={cn("flex items-center", {
          "w-full": !isLast,
          "w-auto": isLast
        }, className)}
        role="listitem"
        aria-current={isActive ? "step" : undefined}
        aria-label={`Step ${stepNumber}${totalSteps ? ` of ${totalSteps}` : ''}${stepTitle ? `: ${stepTitle}` : ''}, ${statusText}`}
        {...props}
      >
        {/* Step Indicator */}
        <div
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full border-2 text-sm font-medium transition-colors flex-shrink-0",
            {
              // Completed step - check icon with primary background
              "bg-primary border-primary text-primary-foreground": isCompleted,
              // Active step - number with primary border
              "border-primary bg-background": isActive && !isCompleted,
              // Inactive step - number with muted border
              "border-muted bg-background text-muted-foreground": !isActive && !isCompleted,
            }
          )}
          aria-hidden="true"
        >
          {isCompleted ? (
            <Check className="h-4 w-4" />
          ) : (
            <span>{stepNumber}</span>
          )}
        </div>

        {/* Step Separator Line */}
        {!isLast && (
          <div
            className={cn(
              "h-0.5 flex-1 transition-colors",
              {
                "bg-primary": isCompleted,
                "bg-muted": !isCompleted,
              }
            )}
          />
        )}
      </div>
    )
  }
)
Step.displayName = "Step"

const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(
  ({ steps, activeStep, className, ...props }, ref) => {
    const stepKeys = Object.keys(steps)
    const activeStepIndex = activeStep === 'complete'
      ? stepKeys.length
      : stepKeys.indexOf(activeStep)

    const activeStepData = steps[activeStep]
    const totalSteps = stepKeys.length
    const currentStepNumber = activeStepIndex + 1

    return (
      <div
        ref={ref}
        className={cn("w-full space-y-4", className)}
        role="group"
        aria-label={`Progress: Step ${currentStepNumber} of ${totalSteps}`}
        {...props}
      >
        {/* Visually hidden live region for screen reader announcements */}
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {activeStepData
            ? `Step ${currentStepNumber} of ${totalSteps}: ${activeStepData.title}`
            : `Completed all ${totalSteps} steps`
          }
        </div>
        {/* Steps Container */}
        <div className="flex w-4/5 mx-auto items-center" role="list" aria-label="Booking steps">
          {stepKeys.map((stepKey, index) => {
            const isCompleted = index < activeStepIndex
            const isActive = index === activeStepIndex
            const isLast = index === stepKeys.length - 1
            const stepData = steps[stepKey]

            return (
              <div key={stepKey} className={cn("flex items-center", {
                "flex-1": !isLast,
                "flex-none": isLast
              })}>
                <Step
                  stepNumber={index + 1}
                  totalSteps={totalSteps}
                  stepTitle={stepData?.title}
                  isActive={isActive}
                  isCompleted={isCompleted}
                  isLast={isLast}
                />
              </div>
            )
          })}
        </div>

        {/* Active Step Title and Description */}
        {activeStepData && (
          <div className="text-center">
            <p className="font-normal text-sm">
              {activeStepData.title}
            </p>
            {activeStepData.description && (
              <p className="font-bold text-sm">
                {activeStepData.description}
              </p>
            )}
          </div>
        )}
      </div>
    )
  }
)
Stepper.displayName = "Stepper"

export { Stepper, Step }
